package br.com.heyagenda.domain.util

object ErrorSanitizer {

    /**
     * Identifica se o erro foi originado por cancelamento intencional do próprio usuário
     * (fechar popup, cancelar FaceID/TouchID, dispensar tela de login social ou paywall).
     */
    fun isCancellation(throwable: Throwable?): Boolean {
        if (throwable == null) return false
        val msg = (throwable.message ?: "").lowercase()
        val className = (throwable::class.simpleName ?: "").lowercase()

        return msg.contains("cancelado") ||
                msg.contains("canceled") ||
                msg.contains("cancelled") ||
                msg.contains("1001") ||
                msg.contains("17058") ||
                msg.contains("user_cancelled") ||
                msg.contains("user cancelled") ||
                msg.contains("cancellation") ||
                className.contains("cancel")
    }

    /**
     * Sanitiza exceções originadas em fluxos de autenticação (E-mail, Google, Apple).
     * Retorna null se o usuário simplesmente cancelou o diálogo (evitando poluir a tela com aviso vermelho).
     */
    fun sanitizeAuthError(throwable: Throwable?): String? {
        if (throwable == null) return null
        if (isCancellation(throwable)) return null

        val raw = throwable.message?.trim() ?: ""
        val msg = raw.lowercase()

        // Se já for uma mensagem amigável em português, preserva
        if (msg.contains("saia e faça login") || msg.contains("login novamente") ||
            msg.contains("motivos de segurança") || msg.contains("excluir sua conta")
        ) {
            return raw
        }

        return when {
            msg.contains("invalid-email") || msg.contains("invalid email") ->
                "Formato de e-mail inválido. Verifique o que digitou."

            msg.contains("user-not-found") || msg.contains("wrong-password") || msg.contains("invalid-credential") ->
                "E-mail ou senha incorretos."

            msg.contains("email-already-in-use") || msg.contains("already in use") ->
                "Este e-mail já está cadastrado em outra conta."

            msg.contains("weak-password") ->
                "A senha deve ter pelo menos 6 caracteres."

            msg.contains("user-disabled") ->
                "Esta conta foi desativada. Entre em contato com o suporte."

            msg.contains("too-many-requests") || msg.contains("quota exceeded") ->
                "Muitas tentativas sem sucesso. Aguarde alguns instantes e tente novamente."

            msg.contains("network") || msg.contains("connection") || msg.contains("timeout") || msg.contains("offline") ->
                "Sem conexão com a internet. Verifique seu sinal e tente novamente."

            msg.contains("google") || msg.contains("apiexception") ->
                "Não foi possível entrar com o Google no momento. Tente novamente em instantes."

            msg.contains("apple") || msg.contains("authorizationservices") ->
                "Não foi possível entrar com a Apple no momento. Tente novamente em instantes."

            msg.contains("requires-recent-login") || msg.contains("recent-login") ||
            msg.contains("requires_recent_login") || msg.contains("credential_too_old") ||
            msg.contains("recent authentication") || msg.contains("requires recent") ||
            msg.contains("operation is sensitive") || msg.contains("log in again before retrying") ->
                "Por motivos de segurança, saia e faça login novamente antes de excluir sua conta."

            else -> "Não foi possível concluir o acesso. Tente novamente em instantes."
        }
    }

    /**
     * Sanitiza erros gerais do app para formulários, cadastros e ações do usuário.
     * Preserva mensagens claras de validação de negócio que já estão em português,
     * e substitui termos técnicos, stacktraces ou exceções de banco pelo fallback humanizado.
     */
    fun sanitize(throwable: Throwable?, fallback: String): String {
        if (throwable == null) return fallback
        if (isCancellation(throwable)) return "Operação cancelada."

        val rawMessage = throwable.message?.trim() ?: return fallback
        val lower = rawMessage.lowercase()

        // 1. Falhas de conectividade / rede
        if (lower.contains("network") || lower.contains("connection") || lower.contains("timeout") ||
            lower.contains("unknownhost") || lower.contains("socket") || lower.contains("offline") ||
            lower.contains("unavailable") || lower.contains("sem conexão")
        ) {
            return "Sem conexão com a internet. Verifique sua rede e tente novamente."
        }

        // 2. Permissões / Sessão / Autenticação recente
        if (lower.contains("recent authentication") || lower.contains("operation is sensitive") ||
            lower.contains("recent-login") || lower.contains("requires recent") ||
            lower.contains("log in again before retrying")
        ) {
            return "Por motivos de segurança, saia e faça login novamente antes de excluir sua conta."
        }

        if (lower.contains("permission_denied") || lower.contains("unauthenticated") || lower.contains("não autenticado")) {
            return "Sua sessão expirou ou você não possui permissão para esta ação."
        }

        // 3. Detectar termos técnicos que indicam mensagens brutas de sistema
        val isTechnical = lower.contains("exception") ||
                lower.contains("error:") ||
                lower.contains("http") ||
                lower.contains("nullpointer") ||
                lower.contains("com.google") ||
                lower.contains("com.apple") ||
                lower.contains("firebase") ||
                lower.contains("firestore") ||
                lower.contains("failed to") ||
                lower.contains("unresolved") ||
                rawMessage.contains("{") ||
                rawMessage.contains("}") ||
                rawMessage.length > 120

        if (isTechnical) {
            return fallback
        }

        // 4. Se a mensagem já é limpa e em português, mantém para orientar o usuário
        return rawMessage.ifBlank { fallback }
    }
}
